'use client'

import { createContext, useContext, useEffect, useState, useCallback } from 'react'

export interface ThemeSettings {
  // Colors
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  textPrimaryColor: string
  textSecondaryColor: string
  // Fonts
  headingFont: string
  bodyFont: string
  // Other
  borderRadius: string
  // Logo
  logoUrl: string | null
  companyName: string
}

const DEFAULT_THEME: ThemeSettings = {
  primaryColor: '#2C3E50',
  secondaryColor: '#8B7355',
  accentColor: '#D4AF37',
  backgroundColor: '#FAFAF8',
  textPrimaryColor: '#1A1A1A',
  textSecondaryColor: '#6B7280',
  headingFont: 'playfair',
  bodyFont: 'inter',
  borderRadius: 'medium',
  logoUrl: null,
  companyName: 'Gedenkportal',
}

// Font family mappings
const FONT_FAMILIES: Record<string, string> = {
  // Heading fonts
  'playfair': "'Playfair Display', Georgia, 'Times New Roman', serif",
  'lora': "'Lora', Georgia, serif",
  'merriweather': "'Merriweather', Georgia, serif",
  'georgia': "Georgia, 'Times New Roman', serif",
  // Body fonts
  'inter': "'Inter', system-ui, -apple-system, sans-serif",
  'open-sans': "'Open Sans', system-ui, sans-serif",
  'roboto': "'Roboto', system-ui, sans-serif",
  'lato': "'Lato', system-ui, sans-serif",
  'source-sans': "'Source Sans 3', system-ui, sans-serif",
}

// Border radius mappings
const BORDER_RADII: Record<string, string> = {
  'none': '0px',
  'small': '4px',
  'medium': '8px',
  'large': '16px',
}

interface ThemeContextType {
  theme: ThemeSettings
  setTheme: (theme: Partial<ThemeSettings>) => void
  refreshTheme: () => Promise<void>
  isLoading: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Helper to apply CSS variables to document
function applyThemeToDocument(theme: ThemeSettings) {
  const root = document.documentElement

  // Colors
  root.style.setProperty('--memorial-primary', theme.primaryColor)
  root.style.setProperty('--memorial-secondary', theme.secondaryColor)
  root.style.setProperty('--memorial-accent', theme.accentColor)
  root.style.setProperty('--memorial-background', theme.backgroundColor)
  root.style.setProperty('--memorial-text-primary', theme.textPrimaryColor)
  root.style.setProperty('--memorial-text-secondary', theme.textSecondaryColor)

  // Derive additional colors
  root.style.setProperty('--memorial-surface', '#FFFFFF')
  root.style.setProperty('--memorial-muted', adjustColor(theme.backgroundColor, -5))
  root.style.setProperty('--memorial-border', adjustColor(theme.backgroundColor, -15))

  // Fonts
  const headingFamily = FONT_FAMILIES[theme.headingFont] || FONT_FAMILIES['playfair']
  const bodyFamily = FONT_FAMILIES[theme.bodyFont] || FONT_FAMILIES['inter']
  root.style.setProperty('--font-serif', headingFamily)
  root.style.setProperty('--font-sans', bodyFamily)

  // Border radius
  const radius = BORDER_RADII[theme.borderRadius] || BORDER_RADII['medium']
  root.style.setProperty('--memorial-radius', radius)
  root.style.setProperty('--memorial-radius-lg', `calc(${radius} * 2)`)
}

// Helper to adjust color brightness
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.max(0, Math.min(255, (num >> 16) + amt))
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt))
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt))
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeSettings>(DEFAULT_THEME)
  const [isLoading, setIsLoading] = useState(true)

  const fetchTheme = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/company')
      if (res.ok) {
        const data = await res.json()
        const newTheme: ThemeSettings = {
          primaryColor: data.memorialPortalPrimaryColor || DEFAULT_THEME.primaryColor,
          secondaryColor: data.memorialPortalSecondaryColor || DEFAULT_THEME.secondaryColor,
          accentColor: data.memorialPortalAccentColor || DEFAULT_THEME.accentColor,
          backgroundColor: data.memorialPortalBackgroundColor || DEFAULT_THEME.backgroundColor,
          textPrimaryColor: data.memorialPortalTextPrimaryColor || DEFAULT_THEME.textPrimaryColor,
          textSecondaryColor: data.memorialPortalTextSecondaryColor || DEFAULT_THEME.textSecondaryColor,
          headingFont: data.headingFont || DEFAULT_THEME.headingFont,
          bodyFont: data.bodyFont || DEFAULT_THEME.bodyFont,
          borderRadius: data.borderRadius || DEFAULT_THEME.borderRadius,
          logoUrl: data.logoUrl || null,
          companyName: data.name || DEFAULT_THEME.companyName,
        }
        setThemeState(newTheme)
        applyThemeToDocument(newTheme)
      }
    } catch (error) {
      console.error('Error fetching theme:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTheme()
  }, [fetchTheme])

  const setTheme = useCallback((updates: Partial<ThemeSettings>) => {
    setThemeState(prev => {
      const newTheme = { ...prev, ...updates }
      applyThemeToDocument(newTheme)
      return newTheme
    })
  }, [])

  const refreshTheme = useCallback(async () => {
    setIsLoading(true)
    await fetchTheme()
  }, [fetchTheme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, refreshTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  )
}
